import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { useEntitlement, type Feature } from "./entitlements";
import { OfflineIdentityContext, rememberedIdentity } from "./offline-identity";
import { can, type Permission } from "./permissions";
import { useOnline } from "./use-online";
import { hasRole, useCurrentUser } from "./use-current-user";

export interface RequireAuthProps {
  children: ReactNode;
  /** Shown while the session is being established or the redirect is in flight. */
  pending: ReactNode;
  /** Shown when Keycloak returned an error instead of a session. */
  onError?: (error: Error) => ReactNode;
  /**
   * What to render instead of sending the browser to an identity provider that is not answering,
   * when this device remembers who was last signed in (US-33.4).
   *
   * <p>Left out, nothing changes: the guard redirects as it always has. Passed, the application
   * below is rendered with the remembered identity in context, offline — which only
   * `roastery-app` asks for, because only it has screens that mean anything with no server.
   */
  offline?: ReactNode;
}

/** How long to wait for the identity provider before deciding it is not there. */
const PROBE_TIMEOUT_MS = 3000;

/**
 * Sends anonymous visitors straight to Keycloak instead of showing them a login form: the identity
 * provider owns the credentials, and with an active SSO cookie the round trip is invisible.
 *
 * <p>Unless it cannot be reached and the caller offered an offline screen (US-33.4). Then the
 * redirect would leave the browser on the identity provider's error page — outside this application,
 * with no way back and nothing on screen — which is the single worst thing a cupping tablet in a
 * cellar can do. So before redirecting, the guard asks: `navigator.onLine` first, because it is free,
 * and then the provider's own discovery document, because "there is a network" and "the roastery's
 * server is up" are different questions and only the second one matters here.
 */
export function RequireAuth({ children, pending, onError, offline }: RequireAuthProps) {
  const auth = useAuth();
  // Read once: the decision must not flip under a screen that has already started drawing.
  const [remembered] = useState(() => (offline ? rememberedIdentity() : null));
  const signedOut = !auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator;
  const mayWorkOffline = Boolean(offline) && remembered !== null;
  const reachable = useIdentityProviderReachable(
    mayWorkOffline && signedOut,
    auth.settings?.authority,
  );

  const shouldRedirect = signedOut && !auth.error && (!mayWorkOffline || reachable === true);

  useEffect(() => {
    if (shouldRedirect) {
      // Come back to the page that was actually requested: without this every deep link would land
      // the user on the dashboard after the round trip through Keycloak.
      void auth.signinRedirect({ redirect_uri: window.location.href });
    }
  }, [shouldRedirect, auth]);

  // Not there, or there and refusing to answer: the remembered user gets their screens back.
  if (mayWorkOffline && signedOut && (reachable === false || auth.error)) {
    return (
      <OfflineIdentityContext.Provider value={remembered}>{offline}</OfflineIdentityContext.Provider>
    );
  }
  if (auth.error) {
    return <>{onError ? onError(auth.error) : null}</>;
  }
  if (!auth.isAuthenticated) {
    return <>{pending}</>;
  }
  return <>{children}</>;
}

/**
 * `undefined` while the question is open, then `true` or `false`.
 *
 * <p>Asked only when it matters — a device with no remembered user has nothing to fall back to, so
 * the extra request would buy nobody anything and delay every sign-in by the round trip.
 */
function useIdentityProviderReachable(enabled: boolean, authority: string | undefined) {
  const online = useOnline();
  const [answer, setAnswer] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!enabled || !online || !authority) {
      return;
    }
    let listening = true;
    fetch(`${authority.replace(/\/$/, "")}/.well-known/openid-configuration`, {
      cache: "no-store",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    })
      .then((response) => {
        if (listening) {
          setAnswer(response.ok);
        }
      })
      .catch(() => {
        if (listening) {
          setAnswer(false);
        }
      });
    return () => {
      listening = false;
    };
  }, [enabled, online, authority]);

  // No network at all is an answer on its own, and a faster one than any request.
  return online ? answer : false;
}

export interface RequireRoleProps {
  /** The user needs at least one of these realm roles. */
  roles: string[];
  children: ReactNode;
  fallback: ReactNode;
  pending?: ReactNode;
}

/** Hides a screen or a fragment from users without the required role (UI half of ADR-006). */
export function RequireRole({ roles, children, fallback, pending = null }: RequireRoleProps) {
  const { user, isLoading } = useCurrentUser();

  if (isLoading) {
    return <>{pending}</>;
  }
  return <>{hasRole(user, ...roles) ? children : fallback}</>;
}

export interface UpsellGateProps {
  /** Entitlement the screen or fragment needs. */
  feature: Feature;
  children: ReactNode;
  /** Shown when the roastery's plan does not include it — an offer, not a refusal. */
  fallback: ReactNode;
  /** Shown while the plan is still unknown; nothing by default, never the offer. */
  pending?: ReactNode;
}

/**
 * UI half of the plan (ADR-006, EP-15): hides what the roastery has not bought and offers it instead.
 * Sibling of {@link RequirePermission}, and deliberately a different component, because the two say
 * different things. A missing permission is "ask your owner" and the screen simply is not yours; a
 * missing entitlement is "this costs money", and the person looking at it may well be the one who
 * decides. So this one shows an offer where that one shows a wall.
 *
 * <p>While the plan is still unknown it renders `pending` — nothing, unless the caller says
 * otherwise. Neither the screen (which would fire the query the plan is about to refuse, leaving a
 * 402 in the console) nor the offer (which would be shown, briefly, to a roastery that has paid).
 */
export function UpsellGate({ feature, children, fallback, pending = null }: UpsellGateProps) {
  const { isLoading } = useCurrentUser();
  const allowed = useEntitlement(feature);
  if (isLoading) {
    return <>{pending}</>;
  }
  return <>{allowed ? children : fallback}</>;
}

export interface RequirePermissionProps {
  /** The user needs all of these; an empty list is never satisfied. */
  permissions: Permission[];
  children: ReactNode;
  fallback: ReactNode;
  pending?: ReactNode;
}

/**
 * Screen-level guard expressed in permissions instead of roles (US-01.5). Prefer it over
 * {@link RequireRole}: a role list in the frontend drifts from the matrix the moment a role gains an
 * operation, while a permission is the same entry the backend checks.
 */
export function RequirePermission({
  permissions,
  children,
  fallback,
  pending = null,
}: RequirePermissionProps) {
  const { user, isLoading } = useCurrentUser();

  if (isLoading) {
    return <>{pending}</>;
  }
  return <>{can(user, ...permissions) ? children : fallback}</>;
}
